import { useEffect, useState } from "react";
import { Company } from "@/types/company";
import { CompanyFiltersState } from "@/providers/contexts/FilterCompaniesContext";
import axiosInstance, { PaginationResponse } from "@/hooks/axios";
import { isAxiosError } from "axios";
import { YEAR_MIN, YEAR_MAX } from "@/providers/contexts";
import * as Sentry from "@sentry/react";

export const COMPANIES_PER_PAGE = 10;

export interface CompanyFilterResponse {
  companyValues: Company[];
  filterValues: CompanyFiltersState;
}

export interface HookResponse extends PaginationResponse {
  results: CompanyFilterResponse;
}

export interface PaginationState extends PaginationResponse {
  totalPages: number;
}

export interface UseCompaniesOptions {
  filters?: CompanyFiltersState;
  skip?: boolean;
  page?: number;
}

export const defaultCompaniesFilters: CompanyFiltersState = {
  selectedCompanies: [],
  selectedHealthcareDomains: [],
  selectedLocations: [],
  selectedCompanySizes: [],
  selectedFundingTypes: [],
  selectedCompanyTypes: [],
  selectedOperatingStatus: [],
  selectedYearRangeMin: YEAR_MIN,
  selectedYearRangeMax: YEAR_MAX,
  search: "",
};

// map camelCase frontend keys to snake_case backend query params
const queryParamMap: Record<string, string> = {
  selectedCompanies: "companies",
  selectedHealthcareDomains: "healthcare_domains",
  selectedLocations: "locations",
  selectedCompanySizes: "company_sizes",
  selectedFundingTypes: "funding_types",
  selectedCompanyTypes: "company_types",
  selectedOperatingStatus: "operating_status",
  selectedYearRangeMin: "year_range_min",
  selectedYearRangeMax: "year_range_max",
  search: "search",
};

export const useCompanies = ({
  filters = defaultCompaniesFilters,
  skip = false,
  page = 1,
}: UseCompaniesOptions = {}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyFilters, setCompanyFilters] = useState<CompanyFiltersState>(
    defaultCompaniesFilters,
  );
  const [pagination, setPagination] = useState<PaginationState>({
    count: 0,
    next: null,
    previous: null,
    totalPages: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skip) return;

    const fetchCompanies = async () => {
      setLoading(true);
      setError(null);

      try {
        const queryParams = Object.entries(filters).reduce(
          (acc, [key, value]) => {
            const backendKey = queryParamMap[key] ?? key;

            if (
              value !== undefined &&
              value !== null &&
              !(Array.isArray(value) && value.length === 0) &&
              value !== ""
            ) {
              if (Array.isArray(value)) {
                // If array contains objects with "id", map to id
                if (
                  value.length &&
                  typeof value[0] === "object" &&
                  "id" in value[0]
                ) {
                  acc[backendKey] = value.map(v => v.id).join(",");
                } else {
                  acc[backendKey] = value.join(",");
                }
              } else {
                acc[backendKey] = value.toString();
              }
            }

            return acc;
          },
          {} as Record<string, string>,
        );

        queryParams.page = page.toString();
        const queryString = new URLSearchParams(queryParams).toString();
        const url = `companies/companies/?${queryString}`;

        const response = await axiosInstance.get<HookResponse>(url);

        if (
          !response.data.results ||
          !response.data.results.companyValues ||
          !response.data.results.filterValues
        ) {
          console.error("Invalid companies API response:", response.data);
          setError("Failed to load companies. Please try again later.");
          setLoading(false);
          return;
        }

        const results = response.data.results;

        setCompanies(prevCompanies =>
          page === 1
            ? results.companyValues
            : [...prevCompanies, ...results.companyValues],
        );

        setCompanyFilters(results.filterValues);

        setPagination({
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          totalPages: Math.ceil(response.data.count / COMPANIES_PER_PAGE),
        });
      } catch (err: unknown) {
        if (isAxiosError(err)) {
          Sentry.captureException(err, {
            contexts: {
              axiosResponse: {
                url: err.config?.url,
                method: err.config?.method,
                status: err.response?.status,
                data: err.response?.data,
              },
            },
          });
          setError(err.message);
        } else if (err instanceof Error) {
          Sentry.captureException(err);
          setError(err.message);
        } else {
          Sentry.captureException(new Error("Unknown error occurred"));
          setError("An unknown error occurred.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [filters, skip, page]);

  return {
    companies,
    companyFilters,
    pagination,
    loading,
    error,
  };
};
